import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import deleteIcon from '../../assets/images/trash.svg';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const HeroImagesManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('Hero image deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete hero image');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(
        section.sectionKey,
        itemId,
        payload,
      );
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (itemId, file) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({
        file: file,
        imageType: 'image',
      });

      await homepageApi.updateItemInSection(
        section.sectionKey,
        itemId,
        payload,
      );
      toastSuccess('Image updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update image');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAltTextChange = async (itemId, altText) => {
    try {
      const payload = getFormDataFromObject({ altText });
      await homepageApi.updateItemInSection(
        section.sectionKey,
        itemId,
        payload,
      );
      toastSuccess('Alt text updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update alt text');
      console.error(error);
    }
  };

  const handleAddNew = async () => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({
        altText: 'New Hero Image',
        status: true,
      });

      await homepageApi.addItemToSection(section.sectionKey, payload);
      toastSuccess('Hero image slot added successfully');
      fetchItems();
      refreshSections();
    } catch (error) {
      toastError('Failed to add hero image');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, {
        items: updatedItems,
      });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems();
    }
  };

  if (loading && !items.length) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                Landing Page CMS
              </li>
              <li>Hero Images Management</li>
            </ol>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top">
                <h3>Hero Images Management</h3>
                <p className="text-muted">
                  Recommended size: 1285x420. Drag to reorder. Click image to
                  change.
                </p>
              </div>
              <div className="middle_main">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleAddNew}
                      disabled={loading}
                    >
                      Add New Hero Image Slot
                    </Button>
                  </div>
                  <Button variant="secondary" onClick={backToList}>
                    Back to Sections
                  </Button>
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="hero_images">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="row"
                      >
                        {sortedItems.map((item, index) => (
                          <Draggable
                            key={item._id}
                            draggableId={item._id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                className="col-md-12 mb-4"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className="card">
                                  <div className="row g-0">
                                    <div className="col-md-8">
                                      <div
                                        style={{
                                          position: 'relative',
                                          height: '250px',
                                          backgroundColor: '#f8f9fa',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          border: '2px dashed #dee2e6',
                                        }}
                                        onClick={() => {
                                          document
                                            .getElementById(
                                              `hero-upload-${item._id}`,
                                            )
                                            .click();
                                        }}
                                      >
                                        {item.image?.location ? (
                                          <img
                                            src={item.image.location}
                                            alt={item.altText || 'Hero Image'}
                                            style={{
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover',
                                            }}
                                          />
                                        ) : (
                                          <div className="text-center text-muted">
                                            <i className="fas fa-cloud-upload-alt fa-3x mb-2"></i>
                                            <p>
                                              Click to upload image (1285x420)
                                            </p>
                                          </div>
                                        )}
                                        <input
                                          type="file"
                                          id={`hero-upload-${item._id}`}
                                          accept="image/*"
                                          style={{ display: 'none' }}
                                          onChange={(e) => {
                                            if (e.target.files[0]) {
                                              handleImageUpload(
                                                item._id,
                                                e.target.files[0],
                                              );
                                            }
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <div className="card-body">
                                        <h6 className="mb-3">
                                          Image #{index + 1}
                                        </h6>

                                        <div className="mb-3">
                                          <label className="form-label small">
                                            Alt Text
                                          </label>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={item.altText || ''}
                                            onChange={(e) =>
                                              setEditingItemId(item._id)
                                            }
                                            onBlur={(e) => {
                                              if (
                                                e.target.value !== item.altText
                                              ) {
                                                handleAltTextChange(
                                                  item._id,
                                                  e.target.value,
                                                );
                                              }
                                              setEditingItemId(null);
                                            }}
                                            placeholder="Enter alt text for accessibility"
                                          />
                                        </div>

                                        <div className="mb-3">
                                          <label className="form-label small d-block">
                                            Status
                                          </label>
                                          <ReactSwitch
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            onChange={() =>
                                              handleToggleStatus(
                                                item._id,
                                                item.status,
                                              )
                                            }
                                            checked={item.status}
                                            height={20}
                                            width={40}
                                          />
                                        </div>

                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={() => handleDelete(item._id)}
                                          className="w-100"
                                        >
                                          <img
                                            src={deleteIcon}
                                            alt="Delete"
                                            width="16"
                                            height="16"
                                            className="me-2"
                                          />
                                          Delete Image
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {sortedItems.length === 0 && (
                  <div className="text-center text-muted py-5">
                    <p>
                      No hero images yet. Click &quot;Add New Hero Image
                      Slot&quot; to get started.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        name="Hero Image"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="Hero Section"
      />
    </div>
  );
};

export default HeroImagesManager;
